import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { ActionIcon, Container, Group, Stack, Title } from 'ui';
import { IconChevronLeft, IconX } from 'ui/Icons';

import { useInAppShortSessionsLayoutContextLayout } from '../../root/Layouts/InAppShortSessionsLayout';
import { CancelDonationContent } from '../Account/components/DonationSettingsModalGroup/modals/CancelDonationModal';
import { EditRecurringDonationModalContent } from '../Account/components/DonationSettingsModalGroup/modals/EditRecurringDonationModal';
import { ManageRecurringDonationsModalContent } from '../Account/components/DonationSettingsModalGroup/modals/ManageRecurringDonationsModal';
import { UpdatePaymentMethodContent } from '../Account/components/DonationSettingsModalGroup/modals/UpdatePaymentMethodModal';

const WebviewPageContainer = ({
  children,
  title,
  action,
}: {
  children: React.ReactNode;
  title: string;
  action?: React.ReactNode;
}) => {
  const navigate = useNavigate();

  return (
    <Container p="lg">
      <Stack>
        <Group gap={4} justify="flex-start" align="center">
          {action ? (
            action
          ) : (
            <ActionIcon size="sm" c="gray" variant="white" onClick={() => navigate(-1)}>
              <IconChevronLeft />
            </ActionIcon>
          )}
          <Title order={2}>{title}</Title>
        </Group>
        {children}
      </Stack>
    </Container>
  );
};

const ManageRecurringDonations = () => {
  const navigate = useNavigate();

  const { closeNativeBrowser } = useInAppShortSessionsLayoutContextLayout();

  const handleCloseSuccess = () => {
    closeNativeBrowser('/donate/manage', 200, 'donation-management');
  };

  return (
    <WebviewPageContainer
      title={'Manage Recurring Donations'}
      action={
        <ActionIcon size="sm" c="gray.7" variant="transparent" onClick={handleCloseSuccess}>
          <IconX brand="tabler" />
        </ActionIcon>
      }
    >
      <ManageRecurringDonationsModalContent handleClick={(uuid) => navigate(`${uuid}`)} />
    </WebviewPageContainer>
  );
};

const EditRecurringDonation = () => {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();

  return (
    <WebviewPageContainer title={'Edit Recurring Donation'}>
      <EditRecurringDonationModalContent
        subscriptionId={subscriptionId}
        onClick={() => navigate('payment')}
        onSuccess={() => navigate(-1)}
        openCancel={() => navigate('cancel')}
      />
    </WebviewPageContainer>
  );
};

const UpdatePaymentMethod = () => {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();

  return (
    <WebviewPageContainer title={'Update Payment Method'}>
      <UpdatePaymentMethodContent subscriptionId={subscriptionId} onSuccess={() => navigate(-1)} />
    </WebviewPageContainer>
  );
};

const CancelDonation = () => {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();

  return (
    <WebviewPageContainer title={'Cancel Donation'}>
      <CancelDonationContent
        subscriptionId={subscriptionId}
        // on successful deletion, we want to go back to view all recurring donations
        onSuccess={() => navigate(-2)}
        onCancel={() => navigate(-1)}
      />
    </WebviewPageContainer>
  );
};

// On desktop, this flow is managed through a modal manager flow. On mobile, we use routes/pages made of modal content.
const ManageRoutes = () => {
  return (
    <Routes>
      <Route index element={<ManageRecurringDonations />} />
      <Route path=":subscriptionId" element={<EditRecurringDonation />} />
      <Route path=":subscriptionId/payment" element={<UpdatePaymentMethod />} />
      <Route path=":subscriptionId/cancel" element={<CancelDonation />} />
    </Routes>
  );
};

export const DonationManagement = () => {
  return <ManageRoutes />;
};
